import React, { useState, useEffect, useCallback } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { Bell, Heart, MessageCircle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { console.error("Failed to load notifications:", e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = async (open) => {
    if (open && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
        setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      } catch (e) { console.error("Failed to mark notifications read:", e); }
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell-button" className="relative h-9 w-9 grid place-items-center rounded-md border border-white/10 text-slate-300 hover:text-white hover:border-brand/40 transition-colors">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-loss text-white font-data text-[10px] font-bold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0C1017] border-white/10 text-slate-200 w-80 p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="font-heading font-bold text-sm">Notifications</span>
          <span className="font-data text-[10px] text-slate-500">{items.length}</span>
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm px-4">No notifications yet. Post in the community to get reactions.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} data-testid="notification-item" className={`flex gap-3 px-4 py-3 border-b border-white/5 ${!n.read ? "bg-brand/5" : ""}`}>
                <div className={`h-8 w-8 shrink-0 rounded-full grid place-items-center ${n.type === "like" ? "bg-loss/15 text-loss" : "bg-brand/15 text-brand"}`}>
                  {n.type === "like" ? <Heart className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 leading-snug">
                    <span className="font-semibold">{n.actor_name}</span>{" "}
                    {n.type === "like" ? "liked your post" : "commented on your post"}
                  </p>
                  {n.comment_excerpt && <p className="text-xs text-slate-400 mt-0.5 truncate">"{n.comment_excerpt}"</p>}
                  <p className="font-data text-[10px] text-slate-600 mt-1 truncate">on "{n.post_excerpt}" · {timeAgo(n.created_at)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
